import {
  FETCH_BOOKINGS,
  FETCH_BOOKINGS_SUCCESS,
  FETCH_BOOKINGS_FAILED,
  UPDATE_BOOKING,
  CANCEL_BOOKING,
  BOOKINGS_PAGE_SIZE
} from "../store/types";
import { fetchBookingLocations } from '../actions/locationactions';
import { RequestPushMsg } from '../other/NotificationFunctions';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { addActualsToBooking, updateDriverQueue, checkDriverActiveBookings } from "../other/sharedFunctions";
import {
  applyStatusTransition,
  canTransition,
  mergeBookingSafe,
  pickPaymentPatch,
  resolveHealedTripStatus,
} from "../other/bookingStateMachine";
import { reverseRouteCoords, reverseWaypoints } from "../other/roundTrip";
import { driverMayBidPrice } from "../other/driverBidChips";
import { get, onValue, update, off, remove, push, runTransaction } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";

const lang = () => store.getState().languagedata.defaultLanguage || {};

/** Commit atómico de transición de status; aborta si la transición es ilegal. */
const commitBookingTransition = async (bookingId, toStatus, patch) => {
  const { singleBookingRef } = firebase;
  let applied = false;
  const result = await runTransaction(singleBookingRef(bookingId), (current) => {
    applied = false;
    // Sin caché local la primera pasada llega en null: devolver null hace que el SDK reintente con el valor real.
    if (current === null) return null;
    const next = applyStatusTransition(current, toStatus, patch);
    if (!next) return;
    applied = true;
    return next;
  });
  return {
    committed: !!(result && result.committed && applied),
    booking: result && result.snapshot ? result.snapshot.val() : null,
  };
};

const clearDriverQueueIfIdle = async (driverId) => {
  if (!driverId) return;
  const { singleUserRef } = firebase;
  const hasActive = await checkDriverActiveBookings(driverId);
  if (!hasActive) {
    update(singleUserRef(driverId), { queue: false });
  }
};

/** Listener actual de lista de viajes (para poder desuscribir al paginar). */
let bookingsListUnsub = null;
let bookingsListQuery = null;

const shouldPageBookings = (usertype) =>
  usertype === 'customer' || usertype === 'driver';

const ACTIVE_BOOKING_STATUSES = [
  'PAYMENT_PENDING', 'NEW', 'ACCEPTED', 'PICKUP_USER', 'ARRIVED',
  'STARTED', 'REACHED', 'PENDING', 'PAID',
];

const bookingSortTs = (b) =>
  Number(b?.bookingDate || b?.tripdate || b?.trip_start_time || 0) || 0;

/**
 * Reserva activa: todas.
 * Realizado / Cancelado: solo los últimos `historyLimit` de cada una (por fecha).
 */
const trimBookingsForLists = (bookings, historyLimit) => {
  const active = [];
  const complete = [];
  const cancelled = [];
  const other = [];
  for (let i = 0; i < bookings.length; i++) {
    const b = bookings[i];
    const st = b?.status;
    if (ACTIVE_BOOKING_STATUSES.indexOf(st) !== -1) active.push(b);
    else if (st === 'COMPLETE') complete.push(b);
    else if (st === 'CANCELLED') cancelled.push(b);
    else other.push(b);
  }
  complete.sort((a, b) => bookingSortTs(b) - bookingSortTs(a));
  cancelled.sort((a, b) => bookingSortTs(b) - bookingSortTs(a));
  // Sin paginación: siempre últimos N por pestaña (carga rápida, sin "Cargar más").
  const kept = [
    ...active,
    ...complete.slice(0, historyLimit),
    ...cancelled.slice(0, historyLimit),
    ...other.slice(0, historyLimit),
  ];
  kept.sort((a, b) => bookingSortTs(b) - bookingSortTs(a));
  return { bookings: kept, active, hasMore: false };
};

const processBookingsSnapshot = (snapshot, userInfo, dispatch, pageLimit) => {
  try {
    if (snapshot.val()) {
      const data = snapshot.val();
      let tracked = null;
      const bookings = Object.keys(data)
        .map((i) => {
          data[i].id = i;
          data[i].pickupAddress = data[i].pickup?.add || '';
          data[i].dropAddress = data[i].drop?.add || '';
          data[i].discount = data[i].discount
            ? data[i].discount
            : 0;
          data[i].cashPaymentAmount = data[i].cashPaymentAmount
            ? data[i].cashPaymentAmount
            : 0;
          data[i].cardPaymentAmount = data[i].cardPaymentAmount
            ? data[i].cardPaymentAmount
            : 0;
          return data[i];
        });
      for (let i = 0; i < bookings.length; i++) {
        if ((['ACCEPTED', 'PICKUP_USER', 'ARRIVED', 'STARTED', 'REACHED', 'PENDING'].indexOf(bookings[i].status) != -1) && userInfo.usertype == 'driver') {
          if (bookings[i].driver === userInfo.uid) {
            tracked = bookings[i];
            fetchBookingLocations(tracked.id)(dispatch);
          }
        }
      }
      const paged = shouldPageBookings(userInfo.usertype) && pageLimit > 0;
      const trimmed = paged
        ? trimBookingsForLists(bookings, pageLimit)
        : {
            bookings: [...bookings].sort((a, b) => bookingSortTs(b) - bookingSortTs(a)),
            active: bookings.filter((b) => ACTIVE_BOOKING_STATUSES.indexOf(b.status) !== -1),
            hasMore: false,
          };
      dispatch({
        type: FETCH_BOOKINGS_SUCCESS,
        payload: {
          bookings: trimmed.bookings,
          active: trimmed.active,
          tracked: tracked,
          pageLimit: paged ? pageLimit : null,
          hasMore: paged ? trimmed.hasMore : false,
        },
      });
      if (tracked) {
        dispatch({
          type: FETCH_BOOKINGS_SUCCESS,
          payload: null
        });
      }
    } else {
      dispatch({
        type: FETCH_BOOKINGS_SUCCESS,
        payload: {
          bookings: [],
          active: [],
          tracked: null,
          pageLimit: pageLimit || BOOKINGS_PAGE_SIZE,
          hasMore: false,
        },
      });
    }
  } catch (err) {
    console.log('[fetchBookings] error processing bookings:', err?.message || err);
    dispatch({
      type: FETCH_BOOKINGS_FAILED,
      payload: err?.message || 'fetch_bookings_error',
    });
  }
};

/**
 * Carga viajes del usuario.
 * Cliente/conductor: ventana RTDB + recorte a últimos 10 Realizado y 10 Cancelado.
 * Admin/fleetadmin: sin límite (dashboard / historial completo).
 */
export const fetchBookings = (limit) => (dispatch) => {
  const {
    bookingListRef,
  } = firebase;

  const userInfo = store.getState().auth.profile;
  if (!userInfo?.uid || !userInfo?.usertype) {
    return;
  }

  const paged = shouldPageBookings(userInfo.usertype);
  const pageLimit = paged
    ? (Number(limit) > 0 ? Number(limit) : BOOKINGS_PAGE_SIZE)
    : null;
  // Ventana RTDB mayor que 10 para no perder activos ni mezclar mal COMPLETE/CANCELLED
  const fetchWindow = paged ? Math.max(pageLimit * 4, 40) : null;

  dispatch({
    type: FETCH_BOOKINGS,
    payload: null,
  });

  if (typeof bookingsListUnsub === 'function') {
    bookingsListUnsub();
    bookingsListUnsub = null;
  } else if (bookingsListQuery) {
    off(bookingsListQuery);
  }

  bookingsListQuery = bookingListRef(userInfo.uid, userInfo.usertype, fetchWindow || undefined);
  bookingsListUnsub = onValue(
    bookingsListQuery,
    (snapshot) => {
      processBookingsSnapshot(snapshot, userInfo, dispatch, pageLimit || 0);
    },
    (err) => {
      console.log('[fetchBookings] listener error:', err?.message || err);
      // Sesión cerrada: el servidor cancela el listener; no reintentar con el usuario anterior.
      if (!firebase.auth?.currentUser || firebase.auth.currentUser.uid !== userInfo.uid) {
        bookingsListUnsub = null;
        bookingsListQuery = null;
        return;
      }
      // Si la query con limit falla, reintentar sin limit y recortar en cliente
      if (!pageLimit) {
        dispatch({
          type: FETCH_BOOKINGS_FAILED,
          payload: err?.message || 'fetch_bookings_error',
        });
        return;
      }
      if (typeof bookingsListUnsub === 'function') {
        try { bookingsListUnsub(); } catch (_) { /* ignore */ }
        bookingsListUnsub = null;
      } else if (bookingsListQuery) {
        try { off(bookingsListQuery); } catch (_) { /* ignore */ }
      }
      bookingsListQuery = bookingListRef(userInfo.uid, userInfo.usertype);
      bookingsListUnsub = onValue(bookingsListQuery, (snapshot) => {
        processBookingsSnapshot(snapshot, userInfo, dispatch, pageLimit);
      });
    }
  );
};

/** Historial fijo a últimos 10; sin expandir (evita salir de Mis Viajes al scrollear). */
export const loadMoreBookings = () => () => {};

/** Marca el modal de éxito visto sin reescribir el booking completo (evita pisar driverOffers). */
export const markConfirmModalSeen = (bookingId) => () => {
  const { singleBookingRef } = firebase;
  if (!bookingId) return;
  update(singleBookingRef(bookingId), { confirmModal: true });
};

/**
 * Escribe una oferta en driverOffers/{uid} sin pisar otras ofertas concurrentes.
 * Rechaza precios bajo customer_min_offer (piso del pasajero) aunque el cliente esté alterado.
 */
export const submitDriverOffer = (bookingId, driverId, offer) => async () => {
  const { singleBookingRef } = firebase;
  if (!bookingId || !driverId || !offer) return false;
  try {
    const snap = await get(singleBookingRef(bookingId));
    const booking = snap.val();
    if (!booking) return false;
    if (booking.status !== 'NEW' && booking.status !== 'PAYMENT_PENDING') return false;
    if (booking.driver && booking.driver !== driverId) return false;
    const tripCost = parseFloat(offer.trip_cost != null ? offer.trip_cost : offer.bidPrice);
    const may = driverMayBidPrice(booking, tripCost);
    if (!may.ok) return false;
    await update(singleBookingRef(bookingId), {
      ['driverOffers/' + driverId]: offer,
    });
    return true;
  } catch (e) {
    console.warn('[submitDriverOffer]', bookingId, e);
    return false;
  }
};

/**
 * Cliente acepta una oferta: transaction (solo NEW sin conductor) + limpia otras ofertas.
 * Fuente de verdad del precio: driverOffers/{uid} en servidor (evita UI stale con la 1.ª oferta).
 * También escribe trip_cost/comisión en la raíz del booking para PaymentDetails y listados.
 */
export const acceptCustomerBid = (bookingId, offer, bookingSnapshot) => async () => {
  const { singleBookingRef } = firebase;
  if (!bookingId || !offer || !offer.driver) return false;

  let accepted = false;
  const result = await runTransaction(singleBookingRef(bookingId), (current) => {
    accepted = false;
    if (current === null) return null;
    if (current.status !== 'NEW' && current.status !== 'PAYMENT_PENDING') return;
    if (current.driver) return;
    if (current.selectedBid && current.selectedBid.driver && current.selectedBid.driver !== offer.driver) {
      return;
    }
    const fromServer =
      current.driverOffers && current.driverOffers[offer.driver]
        ? current.driverOffers[offer.driver]
        : null;
    // Preferir la oferta guardada en RTDB (contraoferta); el payload del cliente puede ir desfasado.
    const acceptedOffer = {
      ...(fromServer && typeof fromServer === 'object' ? fromServer : {}),
      ...offer,
      trip_cost:
        fromServer && fromServer.trip_cost != null && fromServer.trip_cost !== ''
          ? fromServer.trip_cost
          : offer.trip_cost,
      convenience_fees:
        fromServer && fromServer.convenience_fees != null
          ? fromServer.convenience_fees
          : offer.convenience_fees,
      driver_share:
        fromServer && fromServer.driver_share != null
          ? fromServer.driver_share
          : offer.driver_share,
      bidPrice:
        fromServer && fromServer.bidPrice != null
          ? fromServer.bidPrice
          : offer.bidPrice,
      driver: offer.driver,
    };
    const tripCost = parseFloat(acceptedOffer.trip_cost);
    if (!Number.isFinite(tripCost) || tripCost <= 0) return;
    // Blindaje servidor: no aceptar contraoferta bajo el mínimo del pasajero.
    const may = driverMayBidPrice(current, tripCost);
    if (!may.ok) return;

    const next = { ...current };
    next.selectedBid = acceptedOffer;
    next.trip_cost = Number.isFinite(tripCost) ? tripCost : acceptedOffer.trip_cost;
    if (acceptedOffer.convenience_fees != null) next.convenience_fees = acceptedOffer.convenience_fees;
    if (acceptedOffer.driver_share != null) next.driver_share = acceptedOffer.driver_share;
    if (acceptedOffer.bidPrice != null) next.bidPrice = acceptedOffer.bidPrice;
    if (acceptedOffer.fleetCommission != null) next.fleetCommission = acceptedOffer.fleetCommission;

    const offers = { ...(current.driverOffers || {}) };
    Object.keys(offers).forEach((key) => {
      if (key !== offer.driver) {
        delete offers[key];
      }
    });
    offers[offer.driver] = acceptedOffer;
    next.driverOffers = offers;
    const requested = { ...(current.requestedDrivers || {}) };
    Object.keys(requested).forEach((key) => {
      if (key !== offer.driver) {
        delete requested[key];
      }
    });
    next.requestedDrivers = Object.keys(requested).length ? requested : null;
    accepted = true;
    return next;
  });
  return !!(result && result.committed && accepted);
};

/**
 * Inicia el tramo de regreso (ida y vuelta) sin pasar por REACHED ni resetear startTime.
 */
export const startRoundTripReturn = (booking) => async (dispatch) => {
  const { singleBookingRef } = firebase;
  if (!booking || !booking.id) return;
  const returnCoords =
    Array.isArray(booking.returnCoords) && booking.returnCoords.length > 1
      ? booking.returnCoords
      : reverseRouteCoords(booking.coords);
  const returnWaypoints =
    Array.isArray(booking.returnWaypoints) && booking.returnWaypoints.length
      ? booking.returnWaypoints
      : reverseWaypoints(booking.waypoints);
  const patch = {
    roundTripLeg: 'return',
    outboundCompletedAt: Date.now(),
  };
  if (returnCoords) patch.returnCoords = returnCoords;
  if (returnWaypoints) patch.returnWaypoints = returnWaypoints;
  try {
    await update(singleBookingRef(booking.id), patch);
    const snap = await get(singleBookingRef(booking.id));
    const val = snap.val();
    if (val) {
      dispatch({ type: UPDATE_BOOKING, payload: { ...val, id: booking.id } });
    }
  } catch (error) {
    console.warn('[startRoundTripReturn]', booking.id, error);
  }
};

export const updateBooking = (booking) => async (dispatch) => {
  const {
    auth,
    trackingRef,
    singleBookingRef,
    singleUserRef,
    userRatingsRef
  } = firebase;

  if (!booking || !booking.id || !booking.status) {
    console.warn('[updateBooking] booking inválido');
    return { committed: false, reason: 'invalid' };
  }

  const targetStatus = booking.status;
  const L = lang();
  let extraPatch = {};

  let fromStatus = null;
  let beforeVal = null;
  try {
    const before = await get(singleBookingRef(booking.id));
    beforeVal = before.val();
    fromStatus = beforeVal && beforeVal.status;
  } catch (e) {
    fromStatus = null;
  }

  // Si el booking ya quedó inconsistente en RTDB, sanar antes de otra transición.
  const healTo = resolveHealedTripStatus(beforeVal);
  if (healTo && healTo !== fromStatus) {
    const healed = await commitBookingTransition(booking.id, healTo, {});
    if (healed.committed && healed.booking) {
      const payload = { ...healed.booking, id: booking.id };
      dispatch({ type: UPDATE_BOOKING, payload });
      if (healTo === 'PAID' || healTo === 'COMPLETE') {
        await clearDriverQueueIfIdle(payload.driver);
      }
      return { committed: true, booking: payload, healed: true };
    }
  }

  if (targetStatus === 'ARRIVED') {
    extraPatch.driver_arrive_time = new Date().getTime().toString();
  }
  // Solo al entrar a STARTED por primera vez (no al iniciar vuelta del round trip).
  if (targetStatus === 'STARTED' && fromStatus !== 'STARTED') {
    const dt = new Date();
    extraPatch.trip_start_time = dt.getHours() + ':' + dt.getMinutes() + ':' + dt.getSeconds();
    extraPatch.startTime = dt.getTime();
  }
  if (booking.roundTripLeg != null) extraPatch.roundTripLeg = booking.roundTripLeg;
  if (booking.outboundCompletedAt != null) extraPatch.outboundCompletedAt = booking.outboundCompletedAt;
  if (booking.returnCoords != null) extraPatch.returnCoords = booking.returnCoords;
  if (booking.returnWaypoints != null) extraPatch.returnWaypoints = booking.returnWaypoints;
  if (targetStatus === 'REACHED') {
    // Las versiones anteriores de la app guardan la dirección en el cliente; sin esta marca el servidor no la duplica.
    extraPatch.savedAddressOnServer = true;
    // Escribir fin del viaje en la misma TX del status (evita endTime huérfano con status atrasado).
    const startTs = Number(beforeVal?.startTime) || Number(booking.startTime) || 0;
    if (startTs > 0 && !(Number(beforeVal?.endTime) > 0)) {
      const end_time = new Date();
      extraPatch.endTime = end_time.getTime();
      extraPatch.trip_end_time =
        end_time.getHours() + ':' + end_time.getMinutes() + ':' + end_time.getSeconds();
      extraPatch.total_trip_time = Math.abs(Math.round((end_time.getTime() - startTs) / 1000));
    }
  }
  if (targetStatus === 'COMPLETE') {
    if (booking.rating !== undefined) extraPatch.rating = booking.rating;
    if (booking.feedback !== undefined) extraPatch.feedback = booking.feedback;
    if (booking.tipamount !== undefined) extraPatch.tipamount = booking.tipamount;
  }
  if (
    targetStatus === 'PAYMENT_PENDING' ||
    targetStatus === 'NEW' ||
    targetStatus === 'ACCEPTED' ||
    targetStatus === 'PENDING' ||
    targetStatus === 'PAID'
  ) {
    Object.assign(extraPatch, pickPaymentPatch(booking));
  }
  if (targetStatus === 'NEW' || targetStatus === 'ACCEPTED') {
    const queued = updateDriverQueue({ ...booking });
    if (queued && queued.status) {
      // updateDriverQueue puede promover NEW+prepaid+driver → ACCEPTED
    }
  }

  let commitStatus = targetStatus;
  if (targetStatus === 'PAID' && booking.booking_from_web) {
    commitStatus = 'COMPLETE';
  }

  // Limpiar mapas de solicitud al asignar conductor vía pago
  if (commitStatus === 'ACCEPTED' && booking.driver) {
    extraPatch.requestedDrivers = null;
    extraPatch.driverEstimates = null;
  }

  const { committed, booking: committedBooking } = await commitBookingTransition(
    booking.id,
    commitStatus,
    extraPatch
  );

  if (!committed || !committedBooking) {
    console.warn(
      '[updateBooking] Transición rechazada:',
      booking.id,
      '→',
      commitStatus
    );
    return { committed: false, reason: 'rejected', fromStatus, targetStatus: commitStatus };
  }

  const finalBooking = { ...committedBooking, id: booking.id };
  dispatch({
    type: UPDATE_BOOKING,
    payload: finalBooking,
  });

  // Queue del conductor al aceptar
  if (finalBooking.status === 'ACCEPTED' && finalBooking.driver) {
    update(singleUserRef(finalBooking.driver), { queue: true });
  }

  if (finalBooking.status === 'ACCEPTED' && (finalBooking.driver_token || finalBooking.driver)) {
    RequestPushMsg(finalBooking.driver_token, {
      title: L.notification_title || 'Nueva Reserva',
      msg: L.payment_confirmed || 'El cliente confirmó el pago. Ve a recoger al usuario.',
      screen: 'BookedCab',
      params: { bookingId: finalBooking.id },
    }, finalBooking.driver);
  }

  if (finalBooking.status === 'PICKUP_USER' && (finalBooking.customer_token || finalBooking.customer)) {
    RequestPushMsg(finalBooking.customer_token, {
      title: L.notification_title,
      msg: L.driver_on_way || 'El conductor está en camino a recogerte',
      screen: 'BookedCab',
      params: { bookingId: finalBooking.id },
    }, finalBooking.customer);
  }

  if (finalBooking.status === 'ARRIVED' && (finalBooking.customer_token || finalBooking.customer)) {
    RequestPushMsg(finalBooking.customer_token, {
      title: L.notification_title,
      msg: L.driver_near,
      screen: 'BookedCab',
      params: { bookingId: finalBooking.id },
    }, finalBooking.customer);
  }

  if (finalBooking.status === 'STARTED' && fromStatus !== 'STARTED') {
    const driverLocation = store.getState().gpsdata.location;
    if (driverLocation?.lat != null && driverLocation?.lng != null) {
      push(trackingRef(finalBooking.id), {
        at: new Date().getTime(),
        status: 'STARTED',
        lat: driverLocation.lat,
        lng: driverLocation.lng,
      });
    }
    if (finalBooking.customer_token || finalBooking.customer) {
      // Texto fijo: el key driver_journey_msg en RTDB decía "en camino" (confundía con PICKUP_USER).
      RequestPushMsg(finalBooking.customer_token, {
        title: L.notification_title,
        msg: L.driver_journey_msg_started || 'El viaje ha iniciado, ponte el cinturón',
        screen: 'BookedCab',
        params: { bookingId: finalBooking.id },
      }, finalBooking.customer);
    }
  }

  if (finalBooking.status === 'REACHED') {
    const driverLocation = store.getState().gpsdata.location;
    if (driverLocation?.lat != null && driverLocation?.lng != null) {
      push(trackingRef(finalBooking.id), {
        at: new Date().getTime(),
        status: 'REACHED',
        lat: driverLocation.lat,
        lng: driverLocation.lng,
      });
    }
    // Fallback: si la TX no trajo endTime (apps viejas / startTime ausente), completar actuals.
    if (!(Number(finalBooking.endTime) > 0) && Number(finalBooking.startTime) > 0) {
      const bookingObj = await addActualsToBooking(finalBooking, null, driverLocation);
      await update(singleBookingRef(finalBooking.id), {
        trip_end_time: bookingObj.trip_end_time,
        endTime: bookingObj.endTime,
        total_trip_time: bookingObj.total_trip_time,
      });
      finalBooking.trip_end_time = bookingObj.trip_end_time;
      finalBooking.endTime = bookingObj.endTime;
      finalBooking.total_trip_time = bookingObj.total_trip_time;
    }
    if (finalBooking.customer_token || finalBooking.customer) {
      RequestPushMsg(finalBooking.customer_token, {
        title: L.notification_title,
        msg: L.driver_completed_ride,
        screen: 'BookedCab',
        params: { bookingId: finalBooking.id },
      }, finalBooking.customer);
    }
  }

  if (finalBooking.status === 'PENDING' || finalBooking.status === 'PAID' || finalBooking.status === 'COMPLETE') {
    await clearDriverQueueIfIdle(finalBooking.driver);
  }

  if (finalBooking.status === 'PAID' && finalBooking.paymentStatus !== 'unpaid_reported') {
    // Solo al cliente: el conductor ya confirmó el cobro; un push extra deja la burbuja activa.
    if (finalBooking.customer_token || finalBooking.customer) {
      RequestPushMsg(finalBooking.customer_token, {
        title: L.notification_title,
        msg: L.success_payment,
        screen: 'BookedCab',
        params: { bookingId: finalBooking.id },
      }, finalBooking.customer);
    }
  }

  if (finalBooking.status === 'COMPLETE' && finalBooking.rating && finalBooking.driver) {
    if (finalBooking.driver_token || finalBooking.driver) {
      RequestPushMsg(finalBooking.driver_token, {
        title: L.notification_title,
        msg: (L.received_rating || 'X').toString().replace('X', finalBooking.rating.toString()),
        screen: 'BookedCab',
        params: { bookingId: finalBooking.id },
      }, finalBooking.driver);
    }
    onValue(
      userRatingsRef(finalBooking.driver),
      (snapshot) => {
        const ratings = snapshot.val();
        let rating;
        if (ratings) {
          let sum = 0;
          const arr = Object.values(ratings);
          for (let i = 0; i < arr.length; i++) {
            sum = sum + arr[i].rate;
          }
          sum = sum + finalBooking.rating;
          rating = parseFloat(sum / (arr.length + 1)).toFixed(1);
        } else {
          rating = finalBooking.rating;
        }
        update(singleUserRef(finalBooking.driver), { rating: rating });
        push(userRatingsRef(finalBooking.driver), {
          user: finalBooking.customer,
          rate: finalBooking.rating,
          bookingId: finalBooking.id,
        });
      },
      { onlyOnce: true }
    );
  }

  return { committed: true, booking: finalBooking };
};

export const cancelBooking = (data) => async (dispatch) => {
  const {
    singleBookingRef,
    singleUserRef,
    requestedDriversRef
  } = firebase;

  if (!data?.booking?.id) return;

  let cancelledNow = false;
  const result = await runTransaction(singleBookingRef(data.booking.id), (current) => {
    cancelledNow = false;
    if (current === null) return null;
    if (current.status === 'CANCELLED' || current.status === 'COMPLETE') return;
    // Timeout / búsqueda: no cancelar si ya hay oferta aceptada o conductor asignado.
    if (data.onlyIfSearchable) {
      if (current.status !== 'NEW' || current.driver || current.selectedBid) return;
    }
    if (!canTransition(current.status, 'CANCELLED')) return;
    const next = mergeBookingSafe(current, {
      reason: data.reason,
      cancelledBy: data.cancelledBy,
    });
    next.status = 'CANCELLED';
    cancelledNow = true;
    return next;
  });

  if (!result || !result.committed || !cancelledNow) {
    console.warn('[cancelBooking] Cancelación rechazada:', data.booking.id);
    return;
  }

  const cancelled = { ...result.snapshot.val(), id: data.booking.id };
  dispatch({
    type: CANCEL_BOOKING,
    payload: { ...data, booking: cancelled },
  });
  // También UPDATE_BOOKING: quita de active y limpia tracked (mismo camino que otros cierres).
  dispatch({
    type: UPDATE_BOOKING,
    payload: cancelled,
  });

  await clearDriverQueueIfIdle(cancelled.driver);

  // Avisar solo a la otra parte (no al que canceló).
  const L = lang();
  const cancelledBy = data.cancelledBy || cancelled.cancelledBy;
  const driverCancelMsg =
    (cancelledBy === 'customer' && (L.customer_cancelled_booking_ || L.rider_cancel_text))
      ? (L.customer_cancelled_booking_ || L.rider_cancel_text)
      : (L.booking_cancelled + cancelled.id);

  if (cancelledBy !== 'driver' && (cancelled.driver_token || cancelled.driver)) {
    // Conductor ya asignado (viaje aceptado): push + BookedCab muestra C2AlertModal.
    RequestPushMsg(cancelled.driver_token, {
      title: L.notification_title || 'Conecta2',
      msg: driverCancelMsg,
      screen: 'BookedCab',
      params: { bookingId: cancelled.id },
    }, cancelled.driver);
  } else if (cancelledBy === 'customer' && !cancelled.driver) {
    // Solicitud NEW: avisar a quienes tenían la petición / oferta (DriverTrips).
    const notified = new Set();
    const notifyDriverUid = (uid) => {
      if (!uid || notified.has(uid)) return;
      notified.add(uid);
      RequestPushMsg(null, {
        title: L.notification_title || 'Conecta2',
        msg: driverCancelMsg,
        screen: 'DriverTrips',
        params: { bookingId: cancelled.id },
      }, uid);
    };
    const offers = cancelled.driverOffers && typeof cancelled.driverOffers === 'object'
      ? cancelled.driverOffers
      : null;
    if (offers) {
      Object.keys(offers).forEach((uid) => notifyDriverUid(uid));
    }
    const requested = cancelled.requestedDrivers && typeof cancelled.requestedDrivers === 'object'
      ? cancelled.requestedDrivers
      : null;
    if (requested) {
      Object.keys(requested).forEach((uid) => notifyDriverUid(uid));
    }
  }
  if (cancelledBy !== 'customer' && (cancelled.customer_token || cancelled.customer)) {
    const customerMsg =
      (cancelledBy === 'driver' && (L.driver_cancelled_booking_ || L.driver_cancelled_booking))
        ? (L.driver_cancelled_booking_ || L.driver_cancelled_booking)
        : (L.booking_cancelled + cancelled.id);
    RequestPushMsg(cancelled.customer_token, {
      title: L.notification_title || 'Conecta2',
      msg: customerMsg,
      screen: 'BookedCab',
      params: { bookingId: cancelled.id },
    }, cancelled.customer);
  }
  if (data.booking.status === 'NEW' || cancelled.status === 'CANCELLED') {
    try {
      remove(requestedDriversRef(cancelled.id));
    } catch (_) { /* noop */ }
  }
};

export const updateBookingImage = (booking, imageType, imageBlob) => (dispatch) => {
  const {
    singleBookingRef,
    bookingImageRef
  } = firebase;
  uploadBytesResumable(bookingImageRef(booking.id, imageType), imageBlob).then(() => {
    imageBlob.close();
    return getDownloadURL(bookingImageRef(booking.id, imageType));
  }).then((url) => {
    const patch = {};
    if (imageType == 'pickup_image') {
      patch.pickup_image = url;
    }
    if (imageType == 'deliver_image') {
      patch.deliver_image = url;
    }
    update(singleBookingRef(booking.id), patch);
    dispatch({
      type: UPDATE_BOOKING,
      payload: { ...booking, ...patch },
    });
  });
};

export const forceEndBooking = (booking) => async (dispatch) => {
  if (!booking || !booking.id) return;

  // Wallet del conductor lo acredita la Cloud Function (flags idempotentes).
  if (booking.status === 'STARTED') {
    await updateBooking({ ...booking, status: 'REACHED' })(dispatch);
    await updateBooking({
      ...booking,
      status: booking.prepaid ? 'PAID' : 'PENDING',
    })(dispatch);
    return;
  }
  if (booking.status === 'REACHED') {
    await updateBooking({
      ...booking,
      status: booking.prepaid ? 'PAID' : 'PENDING',
    })(dispatch);
  }
};